import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Checkbox,
  Row,
  Col,
  message,
  Spin,
  Button,
  Alert,
  Tag,
} from 'antd';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  AddParticipantPayload,
  ParticipantResponse,
  EditParticipantPayload,
  UpdateAddress,
  Address,
  Estate,
} from '../../types';
import {
  editParticipant,
  addParticipant,
  getParticipant,
  fetchParticipants,
  resendParticipantVerification,
} from './participantService';
import { useGlobalContext } from '../../store/GlobalContext';
import api from '../../services/api';
import { formatEmail } from '../estatelist/estatedetails';
import { formatSSN, SSN_RULE } from '../estatelist/ssn';

const { Option } = Select;

interface AddParticipantModalProps {
  visible: boolean;
  onCancel: () => void;
  /**
   * Fired once the save lands. Editing hands back the updated participant;
   * creating has no equivalent record to return, so the argument is optional
   * and callers that only need to refresh can ignore it.
   */
  onSuccess: (participant?: ParticipantResponse) => void;
  estateId: string;
  estate?: Estate | null;
  initialValues?: ParticipantResponse;
  isEdit?: boolean;
}

/**
 * The relationship values the API accepts, paired with how they read on screen.
 * Family ties are what the family tree draws; the professional roles record who
 * acts for the estate and are shown but not drawn.
 */
const RELATIONSHIP_OPTIONS: { value: RelationshipValue; label: string }[] = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'attorney_for_fiduciary', label: 'Attorney for Fiduciary' },
  { value: 'lawyer', label: 'Lawyer' },
  { value: 'confidential_advisor', label: 'Confidential Advisor' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'financial_advisor', label: 'Financial Advisor' },
];

type RelationshipValue =
  | 'spouse'
  | 'child'
  | 'parent'
  | 'sibling'
  | 'attorney_for_fiduciary'
  | 'lawyer'
  | 'confidential_advisor'
  | 'accountant'
  | 'financial_advisor';

/**
 * Falls back to the raw value so a relationship stored by a newer backend still
 * reads as something rather than as a blank.
 */
const relationshipLabel = (value: string): string =>
  RELATIONSHIP_OPTIONS.find((option) => option.value === value)?.label ??
  value.charAt(0).toUpperCase() + value.slice(1);

interface Relationship {
  id: number;
  uid: string;
  relationship: RelationshipValue;
  createdAt: string;
  updatedAt: string;
  user1: {
    id: number;
    email: string;
    uid: string;
    firstName: string;
    lastName: string;
  };
  user2: {
    id: number;
    email: string;
    uid: string;
    firstName: string;
    lastName: string;
  };
}

const AddParticipantModal: React.FC<AddParticipantModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  estateId,
  estate,
  initialValues,
  isEdit = false,
}) => {
  const [form] = Form.useForm();
  const [relationshipForm] = Form.useForm();
  const [isDeceased, setIsDeceased] = useState(false);
  const [sameAsDomicileForMailing, setSameAsDomicileForMailing] = useState(false);
  const [sameAsDomicileForPresent, setSameAsDomicileForPresent] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [participants, setParticipants] = useState<ParticipantResponse[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [relationshipModalVisible, setRelationshipModalVisible] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);

  const handleResendVerificationClick = async () => {
    if (!initialValues?.uid) return;
    const token = Cookies.get('accessToken') || Cookies.get('token');
    if (!token) {
      message.error('Unauthorized. Please log in.');
      return;
    }
    setResendingVerification(true);
    try {
      const res = await resendParticipantVerification(estateId, initialValues.uid, token);
      Modal.info({
        title: 'Verification Email Sent',
        content: res.message || `A verification email has been resent to ${res.pendingEmail || initialValues.pendingEmail}. Please check your inbox.`,
        okText: 'Understood',
      });
    } catch (err: any) {
      message.error(err.message || err.response?.data?.message || 'Failed to resend verification email.');
    } finally {
      setResendingVerification(false);
    }
  };

  /**
   * Fetch the participant list when the modal is opened.
   */
  useEffect(() => {
    if (visible) {
      const token = Cookies.get('accessToken') || Cookies.get('token');
      if (!token) {
        message.error('Unauthorized. Please log in.');
        return;
      }
      (async () => {
        try {
          const data = await fetchParticipants(token, estateId);
          setParticipants(data);


          console.log(data)
        } catch (err: any) {
          console.error(err);
          message.error(err.message || 'Failed to fetch participants.');
        }
      })();
    }
  }, [visible]);

  /**
   * Build user options for fields that use uid as identifier.
   */
  const userOptions = participants.map((participant) => {
    const fullName = [participant.firstName, participant.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const displayEmailVal = formatEmail(participant.email, '');
    const labelText = fullName
      ? (displayEmailVal ? `${fullName} (${displayEmailVal})` : fullName)
      : (displayEmailVal || 'Unnamed Participant');
    return {
      value: participant.uid,
      label: labelText,
    };
  });


  /**
   * Build relationship user options using numeric id.
   */
  const relationshipUserOptions = participants.map((participant) => {
    const fullName = [participant.firstName, participant.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const displayEmailVal = formatEmail(participant.email, '');
    const labelText = fullName
      ? (displayEmailVal ? `${fullName} (${displayEmailVal})` : fullName)
      : (displayEmailVal || 'Unnamed Participant');
    return {
      value: participant.id,
      label: labelText,
    };
  });


  if (estate && estate.estateOwner) {
    const ownerName = [estate.estateOwner.firstName, estate.estateOwner.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const displayOwnerEmail = formatEmail(estate.estateOwner.email, '');
    const ownerLabel = ownerName
      ? (displayOwnerEmail ? `${ownerName} (Owner - ${displayOwnerEmail})` : `${ownerName} (Owner)`)
      : (displayOwnerEmail ? `${displayOwnerEmail} (Owner)` : 'Estate Owner');

    userOptions.unshift({
      value: estate.estateOwner.uid,
      label: ownerLabel,
    });

    if (!relationshipUserOptions.some((opt) => opt.value === estate.estateOwner.id)) {
      relationshipUserOptions.unshift({
        value: estate.estateOwner.id,
        label: ownerName ? `${ownerName} (Owner)` : 'Estate Owner',
      });
    }
  }

  

  /**
   * Fetch participant details if editing.
   */
  const fetchParticipantDetails = async (participantId: string) => {
    try {
      setFetching(true);
      const token = Cookies.get('accessToken') || Cookies.get('token');
      if (!token) {
        message.error('Unauthorized. Please log in.');
        onCancel();
        setFetching(false);
        return;
      }

      const participantData = await getParticipant(estateId, participantId, token);
      
      form.setFieldsValue({
        ...participantData,
        dob: participantData.dob ? dayjs(participantData.dob) : null,
        dateOfDeath: participantData.dateOfDeath ? dayjs(participantData.dateOfDeath) : null,
      });

      // Extract addresses
      if (participantData.addresses) {
        participantData.addresses.forEach((address) => {
          const atype = address.addressType.toLowerCase();
          if (atype === 'domicile') {
            form.setFieldsValue({
              domicileUid: address.uid,
              domicileAddressLine1: address.addressLine1,
              domicileAddressLine2: address.addressLine2,
              domicileCity: address.city,
              domicileState: address.state,
              domicileZipCode: address.zipCode,
            });
          } else if (atype === 'mailing') {
            form.setFieldsValue({
              mailingUid: address.uid,
              mailingAddressLine1: address.addressLine1,
              mailingAddressLine2: address.addressLine2,
              mailingCity: address.city,
              mailingState: address.state,
              mailingZipCode: address.zipCode,
            });
          } else if (atype === 'present') {
            form.setFieldsValue({
              presentUid: address.uid,
              presentAddressLine1: address.addressLine1,
              presentAddressLine2: address.addressLine2,
              presentCity: address.city,
              presentState: address.state,
              presentZipCode: address.zipCode,
            });
          }
        });
      }

      // Check if mailing/present addresses are same as domicile
      const isMailingSame =
        participantData.mailingAddress &&
        participantData.domicileAddress &&
        participantData.mailingAddress.addressLine1 === participantData.domicileAddress.addressLine1 &&
        participantData.mailingAddress.addressLine2 === participantData.domicileAddress.addressLine2 &&
        participantData.mailingAddress.city === participantData.domicileAddress.city &&
        participantData.mailingAddress.state === participantData.domicileAddress.state &&
        participantData.mailingAddress.zipCode === participantData.domicileAddress.zipCode;

      const isPresentSame =
        participantData.presentAddress &&
        participantData.domicileAddress &&
        participantData.presentAddress.addressLine1 === participantData.domicileAddress.addressLine1 &&
        participantData.presentAddress.addressLine2 === participantData.domicileAddress.addressLine2 &&
        participantData.presentAddress.city === participantData.domicileAddress.city &&
        participantData.presentAddress.state === participantData.domicileAddress.state &&
        participantData.presentAddress.zipCode === participantData.domicileAddress.zipCode;

      setSameAsDomicileForMailing(isMailingSame || false);
      setSameAsDomicileForPresent(isPresentSame || false);
      setIsDeceased(!!participantData.deceased);

      setRelationships(participantData.relationships || []);
      setFetching(false);
    } catch (error: any) {
      console.error(error);
      message.error(error.message || 'Failed to fetch participant details');
      setFetching(false);
    }
  };

  // Trigger detail fetch if editing.
  useEffect(() => {
    if (visible && isEdit && initialValues?.uid) {
      fetchParticipantDetails(initialValues.uid);
    } else if (visible && !isEdit) {
      form.resetFields([]);
      form.setFieldsValue({
        uid: '',
        email: '',
        firstName: '',
        lastName: '',
        dob: null,
        addresses: [],
        relationships: [],
        deceased: false,
        dateOfDeath: null,
        maritalStatus: '',
        specialCircumstance: '',
        suffix: '',
        prefix: '',
        middleName: '',
        ssn: '',
        relatedTo: '',
        relationship: '',
        
      });
      setSameAsDomicileForMailing(false);
      setSameAsDomicileForPresent(false);
      setIsDeceased(false);
      
    }
  }, [visible, isEdit, initialValues, form]);

  // Handle "same as domicile" checkboxes.
  useEffect(() => {
    // If "same as domicile" is checked, copy from domicile fields
    if (sameAsDomicileForMailing) {
      form.setFieldsValue({
        mailingAddressLine1: form.getFieldValue('domicileAddressLine1'),
        mailingAddressLine2: form.getFieldValue('domicileAddressLine2'),
        mailingCity: form.getFieldValue('domicileCity'),
        mailingState: form.getFieldValue('domicileState'),
        mailingZipCode: form.getFieldValue('domicileZipCode'),
      });
    } else if (!isEdit) {
      form.resetFields([
        'mailingAddressLine1',
        'mailingAddressLine2',
        'mailingCity',
        'mailingState',
        'mailingZipCode',
      ]);
    }

    if (sameAsDomicileForPresent) {
      form.setFieldsValue({
        presentAddressLine1: form.getFieldValue('domicileAddressLine1'),
        presentAddressLine2: form.getFieldValue('domicileAddressLine2'),
        presentCity: form.getFieldValue('domicileCity'),
        presentState: form.getFieldValue('domicileState'),
        presentZipCode: form.getFieldValue('domicileZipCode'),
      });
    } else if (!isEdit) {
      form.resetFields([
        'presentAddressLine1',
        'presentAddressLine2',
        'presentCity',
        'presentState',
        'presentZipCode',
      ]);
    }
  }, [sameAsDomicileForMailing, sameAsDomicileForPresent, form, isEdit]);

  const handleFinish = async (values: any) => {
    try {
      setLoading(true);
      const token = Cookies.get('accessToken') || Cookies.get('token');
      if (!token) {
        message.error('Unauthorized. Please log in.');
        setLoading(false);
        onCancel();
        return;
      }

      /**
       * Always include all three addresses, adjusting the lines
       * if "same as domicile" is checked for mailing/present.
       */
      console.log('values', values);
      const domicileAddress: UpdateAddress = {
        addressType: 'domicile',
        uid: values.domicileUid,
        addressLine1: values.domicileAddressLine1,
        addressLine2: values.domicileAddressLine2,
        city: values.domicileCity,
        state: values.domicileState,
        zipCode: values.domicileZipCode,
      };
      const mailingAddress: UpdateAddress = {
        addressType: 'mailing',
        uid: values.mailingUid,
        addressLine1: sameAsDomicileForMailing
          ? values.domicileAddressLine1
          : values.mailingAddressLine1,
        addressLine2: sameAsDomicileForMailing
          ? values.domicileAddressLine2
          : values.mailingAddressLine2,
        city: sameAsDomicileForMailing ? values.domicileCity : values.mailingCity,
        state: sameAsDomicileForMailing ? values.domicileState : values.mailingState,
        zipCode: sameAsDomicileForMailing ? values.domicileZipCode : values.mailingZipCode,
      };
      const presentAddress: UpdateAddress = {
        addressType: 'present',
        uid: values.presentUid,
        addressLine1: sameAsDomicileForPresent
          ? values.domicileAddressLine1
          : values.presentAddressLine1,
        addressLine2: sameAsDomicileForPresent
          ? values.domicileAddressLine2
          : values.presentAddressLine2,
        city: sameAsDomicileForPresent ? values.domicileCity : values.presentCity,
        state: sameAsDomicileForPresent ? values.domicileState : values.presentState,
        zipCode: sameAsDomicileForPresent ? values.domicileZipCode : values.presentZipCode,
      };

      // We'll always pass all three, so .filter(Boolean) is no longer needed
      const addresses = [domicileAddress, mailingAddress, presentAddress];

      if (isEdit && initialValues?.uid) {
        // Edit mode payload
        const payload: EditParticipantPayload = {
          email: values.email,
          firstName: values.firstName,
          middleName: values.middleName,
          lastName: values.lastName,
          prefix: values.prefix,
          suffix: values.suffix,
          dob: values.dob ? values.dob.format('YYYY-MM-DD') : undefined,
          addresses,
          // Never send the masked display value back — it would overwrite the
          // encrypted SSN on the server. Only a freshly typed value goes up.
          ssn: values.ssn && values.ssn.includes('*') ? undefined : values.ssn,
          maritalStatus: values.maritalStatus,
          deceased: isDeceased,
          dateOfDeath: isDeceased && values.dateOfDeath ? values.dateOfDeath.format('YYYY-MM-DD') : undefined,
          specialCircumstance: values.specialCircumstance,
        };


        const updatedParticipant = await editParticipant(estateId, initialValues.uid, payload, token);
        onSuccess(updatedParticipant);
        if (updatedParticipant.pendingEmail) {
          Modal.info({
            title: 'Email Verification Required',
            content: (
              <div>
                <p>Participant profile updated successfully.</p>
                <p>
                  A verification email has been sent to <strong>{updatedParticipant.pendingEmail}</strong>.
                  The email address will remain unverified until confirmed via the link in the email.
                </p>
              </div>
            ),
            okText: 'Understood',
          });
        } else {
          message.success('Participant updated successfully');
        }
      } else {
        // Create mode payload
        const payload: AddParticipantPayload = {
          email: values.email,
          firstName: values.firstName,
          middleName: values.middleName,
          lastName: values.lastName,
          prefix: values.prefix,
          suffix: values.suffix,
          dob: values.dob ? values.dob.format('YYYY-MM-DD') : '',
          relatedTo: values.relatedTo,
          natureOfRelation: values.relationship,
          addresses: addresses as Address[],
          ssn: values.ssn,
          maritalStatus: values.maritalStatus,
          deceased: isDeceased,
          dateOfDeath: isDeceased && values.dateOfDeath ? values.dateOfDeath.format('YYYY-MM-DD') : undefined,
          specialCircumstance: values.specialCircumstance,
        };

        await addParticipant(estateId, payload, token);
        onSuccess();

        message.success('Participant added successfully');
      }

      form.resetFields();
      setSameAsDomicileForMailing(false);
      setSameAsDomicileForPresent(false);
      setIsDeceased(false);
      onCancel();
    } catch (err: any) {
      console.error(err);
      message.error(
        err.message ||
          (isEdit ? 'Failed to update participant.' : 'Failed to add participant.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFinishFailed = (errorInfo: any) => {
    console.log('Form Validation Failed:', errorInfo);
    message.error('Please fill all required fields correctly.');
  };

  const handleAddRelationship = async (values: any) => {
    try {
      if (!initialValues?.id) {
        message.error('User ID missing.');
        return;
      }
      const payload = {
        user1: initialValues.id,
        user2: values.relatedUser,
        relationship: values.relationship,
      };
      const newRelationship = await addRelationship(payload, estate?.uuid || '');
      setRelationships([...relationships, newRelationship]);
      message.success('Relationship added successfully');
      setRelationshipModalVisible(false);
      relationshipForm.resetFields();
    } catch (err: any) {
      console.error(err);
      message.error(err.message || 'Failed to add relationship.');
    }
  };

  const handleDeleteRelationship = async (relationshipId: string) => {
    try {
      const token = Cookies.get('accessToken') || Cookies.get('token');
      if (!token) {
        message.error('Unauthorized. Please log in.');
        return;
      }
      await deleteRelationship(relationshipId, token);
      setRelationships(relationships.filter((rel) => rel.uid !== relationshipId));
      message.success('Relationship deleted successfully');
    } catch (err: any) {
      console.error(err);
      message.error(err.message || 'Failed to delete relationship.');
    }
  };

  return (
    <>
      <Modal
        title={isEdit ? 'Edit Participant' : 'Add Participant'}
        open={visible}
        onCancel={onCancel}
        onOk={() => form.submit()}
        okText={isEdit ? 'Save Changes' : 'Add Participant'}
        cancelText="Cancel"
        confirmLoading={loading || fetching}
        destroyOnClose
      >
        {loading || fetching ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spin tip={isEdit ? 'Loading participant details...' : 'Adding participant...'} />
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onFinish={handleFinish}
            onFinishFailed={handleFinishFailed}
          >
            {isEdit && initialValues?.pendingEmail && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: '1.5rem' }}
                message="Email Verification Pending"
                description={
                  <div>
                    <p style={{ margin: '0 0 8px 0' }}>
                      Change to <strong>{initialValues.pendingEmail}</strong> is unverified. Current email remains <strong>{initialValues.email}</strong>.
                    </p>
                    <Button
                      size="small"
                      type="primary"
                      ghost
                      loading={resendingVerification}
                      onClick={handleResendVerificationClick}
                    >
                      Resend Verification Email
                    </Button>
                  </div>
                }
              />
            )}
            <Form.Item
              label="Email"
              name="email"
              rules={[{ required: true, type: 'email', message: 'Please enter a valid email!' }]}
              help={initialValues?.pendingEmail ? `Pending Verification: ${initialValues.pendingEmail} (Verification email sent)` : undefined}
              validateStatus={initialValues?.pendingEmail ? 'warning' : undefined}
            >
              <Input placeholder="Enter participant's email" />
            </Form.Item>

            {isEdit && (
              <div style={{ marginBottom: '1rem' }}>
                <h3>Relationships</h3>
                {relationships.length > 0 ? (
                  relationships.map((rel) => {
                    const primaryRefUser = rel.user1;
                    const primaryUserName = primaryRefUser
                      ? [primaryRefUser.firstName, primaryRefUser.lastName].filter(Boolean).join(' ')
                      : 'Unknown';
                    const relatedUser = rel.user2;
                    const relatedUserName = relatedUser
                      ? [relatedUser.firstName, relatedUser.lastName].filter(Boolean).join(' ')
                      : 'Unknown';
                    return (
                      <div
                        key={rel.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.5rem 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <span> {primaryUserName} is the{' '}
                          {relationshipLabel(rel.relationship)}{' '}
                          of {relatedUserName}
                        </span>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteRelationship(rel.uid)}
                        />
                      </div>
                    );
                  })
                ) : (
                  <p>No relationships added.</p>
                )}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setRelationshipModalVisible(true)}
                  style={{ marginTop: '0.5rem' }}
                >
                  Add Relationship
                </Button>
              </div>
            )}

            {/* Show "Related To" and "Nature of Relationship" only in create mode */}
            {!isEdit && (
              <>
                <Form.Item label="Related To" name="relatedTo">
                  <Select
                    showSearch
                    placeholder="Search and select a user"
                    optionFilterProp="label"
                    options={userOptions}
                    allowClear
                  />
                </Form.Item>

                <Form.Item label="Nature of Relationship" name="relationship">
                  <Select placeholder="Select relationship" allowClear>
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            <Form.Item label="Special Circumstance" name="specialCircumstance">
              <Input.TextArea rows={2} placeholder="Enter any special circumstances" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={4}>
                <Form.Item label="Prefix" name="prefix">
                  <Input placeholder="e.g., Mr., Ms." />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: 'Please enter the first name!' }]}
                >
                  <Input placeholder="Enter first name" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Middle Name" name="middleName">
                  <Input placeholder="Enter middle name (optional)" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: 'Please enter the last name!' }]}
                >
                  <Input placeholder="Enter last name" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Suffix" name="suffix">
                  <Input placeholder="e.g., Jr., Sr." />
                </Form.Item>
              </Col>
            </Row>

            {/* DOMICILE Address */}
            <Form.Item label="Domicile Address" style={{ fontWeight: 'bold' }} />
            <Form.Item name="domicileUid" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="Address Line 1" name="domicileAddressLine1">
              <Input placeholder="Enter domicile address line 1" />
            </Form.Item>
            <Form.Item label="Address Line 2" name="domicileAddressLine2">
              <Input placeholder="Enter domicile address line 2 (optional)" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="City" name="domicileCity">
                  <Input placeholder="Enter city" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="State" name="domicileState">
                  <Input placeholder="Enter state" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="ZipCode" name="domicileZipCode">
                  <Input placeholder="Enter zipcode" />
                </Form.Item>
              </Col>
            </Row>

            {/* MAILING Address */}
            <Form.Item label="Mailing Address" style={{ fontWeight: 'bold' }}>
              <Checkbox
                onChange={(e) => setSameAsDomicileForMailing(e.target.checked)}
                checked={sameAsDomicileForMailing}
              >
                Same as Domicile Address
              </Checkbox>
            </Form.Item>
            <Form.Item name="mailingUid" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="Address Line 1" name="mailingAddressLine1">
              <Input
                placeholder="Enter mailing address line 1"
                disabled={sameAsDomicileForMailing}
              />
            </Form.Item>
            <Form.Item label="Address Line 2" name="mailingAddressLine2">
              <Input
                placeholder="Enter mailing address line 2 (optional)"
                disabled={sameAsDomicileForMailing}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="City" name="mailingCity">
                  <Input placeholder="Enter city" disabled={sameAsDomicileForMailing} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="State" name="mailingState">
                  <Input placeholder="Enter state" disabled={sameAsDomicileForMailing} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="ZipCode" name="mailingZipCode">
                  <Input placeholder="Enter zipcode" disabled={sameAsDomicileForMailing} />
                </Form.Item>
              </Col>
            </Row>

            {/* PRESENT Address */}
            <Form.Item label="Present Address" style={{ fontWeight: 'bold' }}>
              <Checkbox
                onChange={(e) => setSameAsDomicileForPresent(e.target.checked)}
                checked={sameAsDomicileForPresent}
              >
                Same as Domicile Address
              </Checkbox>
            </Form.Item>
            <Form.Item name="presentUid" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="Address Line 1" name="presentAddressLine1">
              <Input
                placeholder="Enter present address line 1"
                disabled={sameAsDomicileForPresent}
              />
            </Form.Item>
            <Form.Item label="Address Line 2" name="presentAddressLine2">
              <Input
                placeholder="Enter present address line 2 (optional)"
                disabled={sameAsDomicileForPresent}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="City" name="presentCity">
                  <Input placeholder="Enter city" disabled={sameAsDomicileForPresent} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="State" name="presentState">
                  <Input placeholder="Enter state" disabled={sameAsDomicileForPresent} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="ZipCode" name="presentZipCode">
                  <Input placeholder="Enter zipcode" disabled={sameAsDomicileForPresent} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Social Security Number"
              name="ssn"
              normalize={formatSSN}
              rules={[SSN_RULE]}
              extra="For security, SSNs are stored encrypted and displayed masked (***-**-1234). The full number is only used when generating court documents. To change it, type the new full SSN; leaving the masked value keeps the stored one."
            >
              <Input placeholder="123-45-6789" maxLength={11} />
            </Form.Item>
            <Form.Item label="Date of Birth" name="dob">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date of birth" />
            </Form.Item>
            <Form.Item label="Marital Status" name="maritalStatus">
              <Select placeholder="Select marital status" allowClear>
                <Option value="single">Single</Option>
                <Option value="married">Married</Option>
                <Option value="divorced">Divorced</Option>
                <Option value="widowed">Widowed</Option>
              </Select>
            </Form.Item>

            <Form.Item name="isDeceased" valuePropName="checked">
              <Checkbox onChange={(e) => setIsDeceased(e.target.checked)}>
                Participant/Benefactor is Deceased
              </Checkbox>
            </Form.Item>
            {isDeceased && (
              <Form.Item label="Date of Death" name="dateOfDeath">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date of death" />
              </Form.Item>
            )}
          </Form>
        )}
      </Modal>

      {/* Modal for Adding a New Relationship */}
      <Modal
        title="Add Relationship"
        open={relationshipModalVisible}
        onCancel={() => {
          setRelationshipModalVisible(false);
          relationshipForm.resetFields();
        }}
        onOk={() => relationshipForm.submit()}
        okText="Add"
        cancelText="Cancel"
        zIndex={2000}
      >
        <Form form={relationshipForm} layout="vertical" onFinish={handleAddRelationship}>
          <Form.Item
            label="Select Person"
            name="relatedUser"
            rules={[{ required: true, message: 'Please select a person!' }]}
          >
            <Select
              showSearch
              placeholder="Select a user"
              optionFilterProp="label"
              options={relationshipUserOptions.filter((option) => option.value !== initialValues?.id)}
            />
          </Form.Item>
          <Form.Item
            label="Nature of Relationship"
            name="relationship"
            rules={[{ required: true, message: 'Please select the nature of the relationship!' }]}
          >
            <Select placeholder="Select relationship">
              {RELATIONSHIP_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddParticipantModal;


/**
 * New service function to add a relationship.
 */
async function addRelationship(
  payload: { user1: number; user2: number; relationship: string },
  estateId: string
): Promise<Relationship> {
  try {
    const response = await api.post<Relationship>(`relationships/${estateId}`, payload);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to add relationship');
    } else {
      throw new Error('Failed to add relationship');
    }
  }
}

/**
 * New service function to delete a relationship.
 */
async function deleteRelationship(relationshipId: string, token: string): Promise<void> {
  try {
    await api.delete(`/relationships/${relationshipId}`);
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to delete relationship');
    } else {
      throw new Error('Failed to delete relationship');
    }
  }
}
